import {
  ALL_FILTER,
  AnyContent,
  ARTICLE_TYPE,
  GIF_TYPE,
  PHOTO_TYPE,
  STATUS_TYPE,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { STOCK_ALL_FILTER, STOCK_GIF_TYPE, STOCK_PHOTO_TYPE, STOCK_VIDEO_TYPE } from 'interfaces/Content/StockContentTypes'
import { FilterType } from 'shared'

export interface CarouselContentBucket {
  items: AnyContent[]
  page: number
  hasMore: boolean
}

export type CarouselContentState = {
  [key: string]: CarouselContentBucket
}

interface BucketDefinition {
  key: FilterType
  predicate: (item: any) => boolean
}

const byType = (type: FilterType) => (item: any) => item.type === type

// Single source of truth for the content buckets a carousel maintains. Adding a
// new content type is one line here instead of three parallel edits.
export const CONTENT_BUCKETS: BucketDefinition[] = [
  { key: PHOTO_TYPE, predicate: byType(PHOTO_TYPE) },
  { key: ARTICLE_TYPE, predicate: byType(ARTICLE_TYPE) },
  { key: VIDEO_TYPE, predicate: byType(VIDEO_TYPE) },
  { key: GIF_TYPE, predicate: byType(GIF_TYPE) },
  { key: STOCK_PHOTO_TYPE, predicate: byType(STOCK_PHOTO_TYPE) },
  { key: STOCK_VIDEO_TYPE, predicate: byType(STOCK_VIDEO_TYPE) },
  { key: STOCK_GIF_TYPE, predicate: byType(STOCK_GIF_TYPE) },
  { key: STOCK_ALL_FILTER, predicate: (item: any) => item.isStock },
  { key: STATUS_TYPE, predicate: byType(STATUS_TYPE) },
  { key: TEXT_TYPE, predicate: byType(TEXT_TYPE) },
  { key: ALL_FILTER, predicate: () => true }
]

// Fresh bucket set from a list of items: page 0, hasMore true for every bucket.
export const getInitialContentState = (contentItems: AnyContent[]): CarouselContentState => {
  const items: any[] = contentItems || []
  const content: CarouselContentState = {}

  CONTENT_BUCKETS.forEach(({ key, predicate }) => {
    content[key] = { items: items.filter(predicate), page: 0, hasMore: true }
  })

  return content
}

// Re-bucket a fresh list of items while preserving each bucket's paging cursor.
export const getUpdatedContentState = (
  contentItems: AnyContent[],
  currentContent: CarouselContentState
): CarouselContentState => {
  const items: any[] = contentItems || []
  const content: CarouselContentState = {}

  CONTENT_BUCKETS.forEach(({ key, predicate }) => {
    content[key] = {
      items: items.filter(predicate),
      page: currentContent[key].page,
      hasMore: currentContent[key].hasMore
    }
  })

  return content
}

// Append the newly fetched items of every bucket onto the current ones.
export const mergeContentState = (
  currentContent: CarouselContentState,
  newContent: CarouselContentState
): CarouselContentState => {
  const content: CarouselContentState = {}

  CONTENT_BUCKETS.forEach(({ key }) => {
    content[key] = {
      ...currentContent[key],
      items: currentContent[key].items.concat(newContent[key].items)
    }
  })

  return content
}
